/**
 * The console's half of the operator connector's OAuth flow.
 *
 * claude.ai reads the operator's protected-resource metadata, follows its
 * `authorization_servers` entry to THIS host and runs the authorization-code
 * flow here. The auth Worker sends a signed-out visitor to `/oauth/sign-in` and
 * a signed-in one to `/oauth/consent`, each carrying Better Auth's signed
 * authorize query. Everything is same-origin and RELATIVE on purpose: the
 * session cookie is host-only, so every call has to be made to the host that
 * holds it (see `worker/auth-proxy.ts`).
 *
 * This is NOT the console's own login. Do not confuse it with the deleted
 * PKCE sign-in (goal 229): the console still signs in with email and password
 * against the same-origin proxy, and this only adds the two screens an MCP
 * client's authorization needs.
 */

export const OAUTH_AUTHORIZE_PATH = "/api/auth/oauth2/authorize";
export const OAUTH_CONSENT_PATH = "/api/auth/oauth2/consent";
export const OAUTH_PUBLIC_CLIENT_PATH = "/api/auth/oauth2/public-client";
export const OAUTH_SIGN_IN_ROUTE = "/oauth/sign-in";

/** What Better Auth adds when it signs the authorize request it bounces to us. */
const SIGNING_PARAMS = ["sig", "exp", "ba_iat", "ba_param", "ba_pl"];

function stripSearchPrefix(search: string): string {
  return search.startsWith("?") ? search.slice(1) : search;
}

/**
 * The way back into a pending authorization once the admin has signed in: the
 * original request re-sent to the same-origin authorize endpoint, minus Better
 * Auth's signing parameters. The endpoint re-validates the client, redirect URI,
 * PKCE and resource itself, so this is no wider than visiting it directly. Null
 * for any sign-in that is not carrying a signed authorize query.
 */
export function oauthResumePath(search: string): string | null {
  const params = new URLSearchParams(stripSearchPrefix(search));
  if (
    !params.has("sig") ||
    !params.has("response_type") ||
    !params.has("client_id")
  ) {
    return null;
  }
  for (const name of SIGNING_PARAMS) params.delete(name);
  return `${OAUTH_AUTHORIZE_PATH}?${params.toString()}`;
}

/** Back to this console's sign-in page with the same signed query. */
export function signInAgainPath(search: string): string {
  const query = stripSearchPrefix(search);
  return query ? `${OAUTH_SIGN_IN_ROUTE}?${query}` : OAUTH_SIGN_IN_ROUTE;
}

/**
 * The consent request out of the page URL. `oauthQuery` is the query string
 * byte for byte: the provider re-verifies its signature over every parameter,
 * so it is never re-serialised.
 */
export function parseConsentRequest(search: string): {
  clientId: string;
  oauthQuery: string;
  scopes: string[];
} {
  const oauthQuery = stripSearchPrefix(search);
  const params = new URLSearchParams(oauthQuery);
  return {
    clientId: params.get("client_id") ?? "",
    oauthQuery,
    scopes: (params.get("scope") ?? "").split(" ").filter(Boolean),
  };
}

export type SessionVerdict =
  "admin" | "not-admin" | "signed-out" | "unavailable";

/**
 * Who is holding this browser's session, by the console's own gate.
 *
 * `/api/admin/me` is THE admin verdict — the Worker re-derives the role from D1
 * on every request — so consent asks it rather than guessing from a session
 * shape. Plain `fetch`, not `lib/api.ts`: a stale bearer left in this tab would
 * otherwise decide the request, and the approval must be the SESSION's.
 */
export async function fetchSessionVerdict(
  fetchFn: typeof fetch = fetch,
): Promise<SessionVerdict> {
  let response: Response;
  try {
    response = await fetchFn("/api/admin/me", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
  } catch {
    return "unavailable";
  }
  if (response.ok) return "admin";
  if (response.status === 401) return "signed-out";
  if (response.status === 403) return "not-admin";
  return "unavailable";
}

/**
 * The client's display name. The public-client route answers only for a
 * signed-in session and only for the id asked; anything else is an empty name
 * and the page falls back to the client's publisher host.
 */
export async function fetchClientName(
  clientId: string,
  fetchFn: typeof fetch = fetch,
): Promise<string> {
  if (!clientId) return "";
  try {
    const response = await fetchFn(
      `${OAUTH_PUBLIC_CLIENT_PATH}?client_id=${encodeURIComponent(clientId)}`,
      {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      },
    );
    if (!response.ok) return "";
    const body = (await response.json()) as {
      client_id?: unknown;
      client_name?: unknown;
    };
    return body.client_id === clientId && typeof body.client_name === "string"
      ? body.client_name
      : "";
  } catch {
    return "";
  }
}

export type ConsentOutcome =
  | { ok: true; redirectTo: string }
  | { ok: false; reason: "expired" | "signed-out" | "failed"; message: string };

const EXPIRED_MESSAGE =
  "This authorization request is no longer valid. Return to the app you are connecting and start connecting again.";
const SIGNED_OUT_MESSAGE = "Your session has ended. Sign in and try again.";
const FAILED_MESSAGE = "We could not save your choice. Please try again.";

/**
 * Record the decision. The endpoint answers `{redirect: true, url}` for BOTH
 * outcomes: an approval's url carries the authorization code, a denial's carries
 * `error=access_denied`, and either way the browser goes back to the client.
 * Failure families are told apart by body shape, never by echoing the server.
 */
export async function submitConsent(
  decision: { accept: boolean; oauthQuery: string },
  fetchFn: typeof fetch = fetch,
): Promise<ConsentOutcome> {
  let response: Response;
  try {
    response = await fetchFn(OAUTH_CONSENT_PATH, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accept: decision.accept,
        oauth_query: decision.oauthQuery,
      }),
    });
  } catch {
    return { ok: false, reason: "failed", message: FAILED_MESSAGE };
  }
  const body = (await response.json().catch(() => null)) as {
    redirect?: unknown;
    url?: unknown;
    error?: unknown;
  } | null;

  if (response.ok && body?.redirect === true && typeof body.url === "string") {
    return { ok: true, redirectTo: body.url };
  }
  // A tampered or expired signed query, or a scope the request never asked
  // for: signing in again never helps, only the client can start afresh.
  if (
    body?.error === "invalid_signature" ||
    body?.error === "invalid_request"
  ) {
    return { ok: false, reason: "expired", message: EXPIRED_MESSAGE };
  }
  if (response.status === 401) {
    return { ok: false, reason: "signed-out", message: SIGNED_OUT_MESSAGE };
  }
  return { ok: false, reason: "failed", message: FAILED_MESSAGE };
}
